import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Radio } from 'lucide-react';

// YouTube Icon Component
const YouTubeIcon = ({ size = 24, className = '' }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/gallery', label: 'Galerie' },
    { href: '/trainings', label: 'Formations' },
    { href: '/formateurs', label: 'Formateurs' },
    { href: '/partenaires', label: 'Partenaires' },
    { href: '/evenements', label: 'Événements' },
    { href: '/services', label: 'Services' },
    { href: '/documentaire', label: 'Documentaire' },
    { href: '/avis', label: 'Avis' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <span className="font-['Teko'] text-3xl md:text-4xl font-semibold text-[#FACC15] tracking-wider">
              STUNT DIRECTOR
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`font-['Barlow'] uppercase tracking-wider text-xs font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-[#FACC15]'
                    : 'text-white hover:text-[#FACC15]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Radio Button */}
            <Link
              to="/radio"
              data-testid="nav-radio"
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-['Barlow'] uppercase tracking-wider text-xs font-bold transition-all duration-200 ${
                isActive('/radio')
                  ? 'bg-[#FACC15] text-black'
                  : 'bg-[#FACC15]/20 text-[#FACC15] hover:bg-[#FACC15] hover:text-black'
              }`}
            >
              <Radio size={16} className="animate-pulse" />
              Radio
            </Link>
            {/* YouTube Link */}
            <a
              href="https://www.youtube.com/@Alexmaybeplanet"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-[#FF0000] transition-colors duration-200"
              data-testid="nav-youtube"
              title="Notre chaîne YouTube"
            >
              <YouTubeIcon size={22} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white hover:text-[#FACC15] transition-colors"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`font-['Barlow'] uppercase tracking-wider text-sm font-medium py-2 transition-colors duration-200 ${
                    isActive(link.href)
                      ? 'text-[#FACC15]'
                      : 'text-white hover:text-[#FACC15]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Radio Link Mobile */}
              <Link
                to="/radio"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 text-[#FACC15] font-['Barlow'] uppercase tracking-wider text-sm font-bold py-2"
                data-testid="mobile-nav-radio"
              >
                <Radio size={18} className="animate-pulse" />
                Stunt Radio
              </Link>
              {/* YouTube Link Mobile */}
              <a
                href="https://www.youtube.com/@Alexmaybeplanet"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white hover:text-[#FF0000] font-['Barlow'] uppercase tracking-wider text-sm font-medium py-2 transition-colors duration-200"
                data-testid="mobile-nav-youtube"
              >
                <YouTubeIcon size={18} />
                YouTube
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
